// AI-CONTEXT: See docs/features/updates-log.md
// Vormcontrole op het `highlight`-blok van een update: het uitgelichte verhaal
// met een schermafbeelding, dat een eigen pagina krijgt.
//
// Aparte helper en geen uitbreiding van isValidUpdate: een update zónder
// highlight is nog steeds volkomen geldig, en die twee vragen moeten los van
// elkaar te beantwoorden zijn.

/** @param {*} value @returns {boolean} */
const isGevuldeTekst = (value) => typeof value === 'string' && value.trim().length > 0

/**
 * De afbeelding moet een gewone bestandsnaam zijn uit `public/updates/`.
 *
 * Een pad naar buiten zou het uitgelichte verhaal een baken maken: het beeld
 * wordt dan bij elke lezer van een vreemde server gehaald, met zijn IP-adres
 * erbij. En een pad omhoog (`../`) haalt bestanden op die niet voor de lezer
 * bedoeld zijn.
 *
 * @param {*} value
 * @returns {boolean}
 */
const isVeiligeAfbeelding = (value) => {
    if (!isGevuldeTekst(value)) return false
    return /^[a-z0-9][a-z0-9._-]*\.(png|jpg|jpeg|webp)$/i.test(value.trim())
}

/**
 * @typedef {object} HighlightSection
 * @property {string} heading - Kop boven de alinea
 * @property {string} body - De alinea zelf
 * @property {Array<string>} [bullets] - Optionele opsomming
 */

/**
 * @typedef {object} Highlight
 * @property {string} image - Bestandsnaam in public/updates/
 * @property {string} imageAlt - Wat er op de afbeelding te zien is
 * @property {string} lead - Eén alinea die het verhaal opent
 * @property {Array<HighlightSection>} sections - Minstens één sectie
 */

/**
 * @param {*} sectie
 * @returns {boolean}
 */
const isGeldigeSectie = (sectie) => {
    if (!sectie || typeof sectie !== 'object' || Array.isArray(sectie)) return false
    if (!isGevuldeTekst(sectie.heading)) return false
    if (!isGevuldeTekst(sectie.body)) return false

    if (sectie.bullets !== undefined) {
        if (!Array.isArray(sectie.bullets) || sectie.bullets.length === 0) return false
        if (!sectie.bullets.every(isGevuldeTekst)) return false
    }
    return true
}

/**
 * @param {*} highlight - Het `highlight`-blok van een update
 * @returns {boolean} true als het verhaal compleet en veilig te tonen is
 */
const isValidHighlight = (highlight) => {
    if (!highlight || typeof highlight !== 'object' || Array.isArray(highlight)) return false

    // Het beeld draagt het verhaal; zonder beeld is dit een gewone update met
    // een grote kaart eromheen, en dat is precies wat het niet moet zijn.
    if (!isVeiligeAfbeelding(highlight.image)) return false

    // Zonder alt-tekst is dat beeld onzichtbaar voor wie een schermlezer
    // gebruikt — en dan blijft er van het verhaal weinig over.
    if (!isGevuldeTekst(highlight.imageAlt)) return false

    if (!isGevuldeTekst(highlight.lead)) return false

    if (!Array.isArray(highlight.sections) || highlight.sections.length === 0) return false
    if (!highlight.sections.every(isGeldigeSectie)) return false

    return true
}

export default isValidHighlight
